import moment from 'moment';
import React, { useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { useDispatch } from 'react-redux';
import Select from "react-select";
import { toast } from 'react-toastify';
import { Button, Col, Form, Input, Label, Modal, ModalBody, ModalHeader, Row, Spinner } from 'reactstrap';
import { uploadCertificationAPI } from '../../apiConfig/apiCall';
import SampleCSVFileForCertificateUpload from '../../csvFiles/cvp-sample.csv';
import useReactSelectCustomStyle from '../../hooks/useReactSelectCustomStyle';
import { getCertificationList } from '../../slices/ManageCompliance/thunk';

export default function UploadCertificateModal({
    uploadCertificateModal,
    setUploadCertificateModal,
    toggleUploadCertificateModal
}) {

    const dispatch = useDispatch();
    const selectBoxCustomStyles = useReactSelectCustomStyle();

    const [submittingForm, setSubmittingForm] = useState(false); // submittingForm state

    const handleDownloadSampleFile = () => {
        const link = document.createElement('a');
        link.href = SampleCSVFileForCertificateUpload;
        link.download = 'cvp-sample.csv';
        link.click();
    };

    const statusOptions = [
        { value: 'ACTIVE', label: 'ACTIVE' },
        { value: 'INACTIVE', label: 'INACTIVE' },
    ];

    const initialFormData = {
        certificateType: "",
        overview: "",
        launchDate: moment().format('YYYY-MM-DD'),
        status: null,
        csvFile: null
    };

    const [formDataUploadCertificate, setFormDataUploadCertificate] = useState(initialFormData);

    const handleChangeUploadCertificate = (field, value) => {
        if (field === 'certificateType' && value.length > 20) {
            toast.error('Certificate version cannot exceed 20 characters.');
        } else if (field === 'overview' && value.length > 200) {
            toast.error('Overview cannot exceed 200 characters.');
        } else {
            setFormDataUploadCertificate(prevState => ({
                ...prevState,
                [field]: value
            }));
        }
    };

    const handleFileChange = (file) => {
        if (file) {
            const fileExtension = file.name.split('.').pop().toLowerCase();

            if (fileExtension !== 'csv') {
                toast.error("Only CSV files are allowed.");
                return;
            }
            setFormDataUploadCertificate((prevState) => ({
                ...prevState,
                csvFile: file,
            }));
        } else {
            toast.error("Please select a file.");
        }
    };


    const handleReset = () => {
        setFormDataUploadCertificate(initialFormData);
    };

    const handleSubmitUploadCertificate = async (e) => {
        e.preventDefault();
        if (submittingForm) return;
        try {
            const { certificateType, launchDate, overview, status, csvFile } = formDataUploadCertificate;

            if (!certificateType) {
                toast.error('Certificate Type is required');
                return;
            } else if (certificateType.length > 20) {
                toast.error('Certificate Type must be a maximum of 20 characters');
                return;
            }

            else if (!status) {
                toast.error('Status is required');
                return;
            }
            else if (!overview) {
                toast.error('Overview is required');
                return;
            } else if (overview.length > 200) {
                toast.error('Overview must be a maximum of 200 characters');
                return;
            }

            else if (!csvFile) {
                toast.error('CSV File is required');
                return;
            }

            const dataToSubmit = {
                certificationType: certificateType,
                overview,
                launchDate,
                status,
                file: csvFile
            };
            setSubmittingForm(true);
            const apiResponse = await uploadCertificationAPI(dataToSubmit);
            if (apiResponse.status === 200) {
                toast.success(apiResponse.data);
                setUploadCertificateModal(false);
                dispatch(getCertificationList());
            }
            else if (apiResponse.status === 400) {
                toast.error(apiResponse.data);
            }
            else {
                toast.error(apiResponse.data);
            }
        } catch (error) {
            console.error('Error sending data:', error);
            toast.error('An error occurred while upload the certificate');
        } finally {
            setSubmittingForm(false);
        }
    };

    return (
        <>
            <Modal isOpen={uploadCertificateModal} toggle={toggleUploadCertificateModal} size='lg'>
                <ModalHeader toggle={toggleUploadCertificateModal}>
                    Upload Certificate
                    <Button
                        color="primary"
                        className="waves-effect waves-light material-shadow-none mx-3"
                        outline
                        onClick={handleDownloadSampleFile}
                    >
                        <i className="ri-download-2-line me-2" />
                        Download Sample File
                    </Button>
                </ModalHeader>
                <ModalBody>
                    <Form>
                        <Row className="gy-3">
                            <Col lg={6}>
                                <div>
                                    <Label htmlFor="certificateType" className="form-label">Certificate Version</Label>
                                    <Input
                                        type="text"
                                        className="form-control"
                                        id="certificateType"
                                        value={formDataUploadCertificate.certificateType}
                                        onChange={(e) => handleChangeUploadCertificate('certificateType', e.target.value)}
                                    />
                                </div>
                            </Col>
                            <Col lg={6}>
                                <div>
                                    <Label htmlFor="status" className="form-label">Status</Label>
                                    <Select
                                        value={formDataUploadCertificate.status ? statusOptions.find(option => option.value === formDataUploadCertificate.status) : null}
                                        onChange={(option) => handleChangeUploadCertificate('status', option?.value || null)}
                                        options={statusOptions}
                                        styles={selectBoxCustomStyles}
                                    />
                                </div>
                            </Col>
                            <Col lg={6}>
                                <div>
                                    <Label htmlFor="launchDate" className="form-label">Launch Date</Label>
                                    <Flatpickr
                                        className="form-control"
                                        options={{ dateFormat: "Y-m-d" }}
                                        value={formDataUploadCertificate.launchDate}
                                        onChange={(date) => handleChangeUploadCertificate('launchDate', date)}
                                    />
                                </div>
                            </Col>
                            <Col lg={6}>
                                <div>
                                    <Label htmlFor="overview" className="form-label">Overview</Label>
                                    <Input
                                        type="text"
                                        className="form-control"
                                        id="overview"
                                        value={formDataUploadCertificate.overview}
                                        onChange={(e) => handleChangeUploadCertificate('overview', e.target.value)}
                                    />
                                </div>
                            </Col>
                            <Col lg={6}>
                                <div>
                                    <Label htmlFor="csvFile" className="form-label">CSV File</Label>
                                    <Input
                                        type="file"
                                        className="form-control"
                                        id="csvFile"
                                        onChange={(e) => handleFileChange(e.target.files[0])}
                                    />
                                </div>
                            </Col>
                            <Col lg={12}>
                                    {
                                        submittingForm ? <Button color = "primary" className="me-2"  type='button' disabled>
                                            <Spinner size="sm"> Loading... </Spinner> </Button> : <Button color="primary" type="submit" className="me-2" disabled={submittingForm} onClick={handleSubmitUploadCertificate}> Add  </Button>
                                    }
                                <Button color="secondary" type="reset" disabled={submittingForm} onClick={handleReset} > Reset </Button>
                            </Col>
                        </Row>
                    </Form>
                </ModalBody>
            </Modal>
        </>
    )
}